/* eslint-disable react-compiler/react-compiler */
import { useState } from 'react';
import Sticky from 'react-sticky-el';
import { useMediaQuery } from '@mantine/hooks';
import { breakpoints, shadows } from 'styles/theme.variables';
import { useAuth } from 'hooks/useAuth';

import { Box, Card } from '@mantine/core';

import { getFilteredItems } from 'components/MenuManagement/helpers/categoryManagement';
import { MenuData } from 'components/MenuManagement/types';
import { useMenuPreview } from 'hooks/useMenuManagementMenuPreview';
import useCategoryManagement from 'components/MenuManagement/hooks/useCategoryManagement';
import CardEmpty from 'components/CardEmpty/CardEmpty';
import SearchHeader from './SearchHeader';
import CategorySection from './CategorySection';

interface CategoryContainerProps {
  categories?: string[];
}

export default function CategoryContainer({ categories = [] }: CategoryContainerProps) {
  const { menu } = useMenuPreview() as { menu: MenuData };
  const { isInternalUser, isImpersonating } = useAuth();
  const [isShadowVisible, setIsShadowVisible] = useState(false);
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.maxTablet})`) ?? false;

  const {
    categoryRefs,
    filteredCategories,
    searchQuery,
    activeCategoryID,
    setSearchQuery,
    handleCategorySelect,
  } = useCategoryManagement(categories, menu);

  function getStickyTop(isOffset = false) {
    let baseValue;
    if (isInternalUser || isImpersonating) {
      baseValue = isMobile ? 112 : 128;
    } else {
      baseValue = isMobile ? 56 : 64;
    }
    return isOffset ? -baseValue : baseValue;
  }

  return (
    <Card p={0} pb="md">
      <Sticky
        stickyStyle={{
          top: getStickyTop(),
          zIndex: 1,
          background: 'var(--surface-secondary)',
        }}
        topOffset={getStickyTop(true)}
        onFixedToggle={setIsShadowVisible}
      >
        <Box bg="var(--surface-secondary)" pt={{ sm: 'sm', md: 'md' }}>
          <Box
            pt={isShadowVisible ? { sm: 'sm', md: 'md' } : { sm: 'md', md: 'xl' }}
            px={{ sm: 'md', md: 'xl' }}
            pb={isShadowVisible ? { sm: 'sm', md: 'md' } : 'unset'}
            bg="var(--surface-primary)"
            style={{
              borderRadius: 'var(--mantine-radius-sm) var(--mantine-radius-sm) 0 0',
              boxShadow: isShadowVisible ? shadows.bottom : 'none',
            }}
          >
            <SearchHeader
              searchQuery={searchQuery}
              onSearch={setSearchQuery}
              categories={categories}
              menu={menu}
              activeCategoryID={activeCategoryID}
              onCategorySelect={handleCategorySelect}
            />
          </Box>
        </Box>
      </Sticky>

      {filteredCategories.length === 0 ? (
        <Box px="md" pt="xl">
          <CardEmpty />
        </Box>
      ) : (
        filteredCategories.map(categoryID => {
          const category = menu?.categories?.[categoryID];
          const items = getFilteredItems(categoryID, menu, searchQuery);

          return (
            <CategorySection
              key={categoryID}
              category={category}
              items={items}
              isMobile={isMobile}
              categoryRef={el => {
                categoryRefs.current[categoryID] = el;
              }}
            />
          );
        })
      )}
    </Card>
  );
}
